import axios, { AxiosInstance } from 'axios';
import { CookieSetOptions } from 'universal-cookie';

let config: AuthOptionsType;
let axiosInstance: AxiosInstance;
let cookieConfig: CookieSetOptions;

const DEFAULT_PUBLIC_PATHS = ['/', '/login', '/login/', '/reset-password/', '/reset-password'];
const DEFAULT_LOGIN_PATH = '/login';
const DEFAULT_APP_DOMAIN = '.myfiona.com';
const DEFAULT_REDIRECT_AFTER_LOGIN = '/';

/**
 * @type {object}
 * @property {string} apiUrl - The base URL for the authentication API.
 * @property {string} appDomain - The domain of the application. Default: '.myfiona.com'.
 * @property {string[]} [publicPaths] - An array of public paths that do not require authentication. Default: ['/', '/login', '/login/', '/reset-password/', '/reset-password'].
 * @property {string} [loginPath] - The path to the login page. Default: '/login'.
 * @property {string} [defaultRedirectAfterLogin] - The default redirect URL after login. Default: '/'.
 * @property {string} [defaultRedirectAfterLogout] - The default redirect URL after logout. Default: (set to the login path).
 * @property {boolean} [hasRedirectAfterPasswordReset] - Should we redirect to the login page after password reset? Default: false.
 */
type AuthOptionsType = {
  apiUrl: string;
  appDomain: string;
  publicPaths?: string[];
  loginPath?: string;
  defaultRedirectAfterLogin?: string;
  defaultRedirectAfterLogout?: string;
  hasRedirectAfterPasswordReset?: boolean;
  defaultAccountId?: string;
};

/**
 * Initialize the Fiona Auth kit
 * @param {AuthOptionsType} options - The options for the authentication setup.
 * @see {@link AuthOptionsType}
 */
export function initializeAuth(options: AuthOptionsType) {
  config = options;
  config.loginPath = config.loginPath ?? DEFAULT_LOGIN_PATH;
  config.publicPaths = config.publicPaths ?? DEFAULT_PUBLIC_PATHS;
  config.appDomain = config.appDomain ?? DEFAULT_APP_DOMAIN;
  config.defaultRedirectAfterLogin =
    config.defaultRedirectAfterLogin ?? DEFAULT_REDIRECT_AFTER_LOGIN;
  config.defaultRedirectAfterLogout = config.defaultRedirectAfterLogout ?? config.loginPath;
  axiosInstance = axios.create({ baseURL: config.apiUrl });
  // TODO: Confirm cookie age
  const fiveMinutes = 5 * 60 * 1000;
  cookieConfig = { path: '/', secure: true, domain: config.appDomain, maxAge: fiveMinutes };
  config.hasRedirectAfterPasswordReset = options.hasRedirectAfterPasswordReset ?? false;
}

/**
 * Get the base URL for the authentication API from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The base URL for the authentication API.
 */
export function getAuthApiUrl(): string {
  if (!config.apiUrl) {
    throw new Error('Auth base URL is not set. Please call initializeAuth() first.');
  }
  return config.apiUrl;
}

/**
 * Get the domain for setting the cookies from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The domain where the app resides.
 */
export function getAppDomain(): string {
  if (!config.appDomain) {
    throw new Error('App domain is not set. Please call initializeAuth() first.');
  }
  return config.appDomain;
}

/**
 * Get the list of public paths from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The list of paths that are public.
 */
export function getPublicPaths(): string[] {
  if (!config.publicPaths) {
    throw new Error('Public paths are not set. Please call initializeAuth() first.');
  }
  return config.publicPaths;
}

/**
 * Get the login path from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The path to the login form.
 */
export function getLoginPath(): string {
  if (!config.loginPath) {
    throw new Error('Login path is not set. Please call initializeAuth() first.');
  }
  return config.loginPath;
}

/**
 * Get the default redirect after login path from the configuration from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The default path to redirect to after successful login.
 */
export function getDefaultRedirectAfterLogin(): string {
  if (!config.defaultRedirectAfterLogin) {
    throw new Error('Default redirect after login is not set. Please call initializeAuth() first.');
  }
  return config.defaultRedirectAfterLogin;
}

/**
 * Get the default redirect after logout path from the configuration.
 * @throws Will throw an error if the auth kit is not initialized.
 * @returns {string} The default path to redirect to after successful logout.
 */
export function getDefaultRedirectAfterLogout(): string {
  if (!config.defaultRedirectAfterLogout) {
    throw new Error(
      'Default redirect after logout is not set. Please call initializeAuth() first.',
    );
  }
  return config.defaultRedirectAfterLogout;
}

export function getAxiosInstance(): AxiosInstance {
  if (!axiosInstance) {
    throw new Error('Cannot call back-end api. Please call initializeAuth() first.');
  }
  return axiosInstance;
}

export function getCookieConfig(): CookieSetOptions {
  if (!cookieConfig) {
    throw new Error('Cannot set cookies. Please call initializeAuth() first.');
  }
  return cookieConfig;
}

export function getHasRedirectAfterPasswordReset(): boolean {
  if (config.hasRedirectAfterPasswordReset === undefined) {
    throw new Error(
      'Cannot check redirect after password reset. Please call initializeAuth() first.',
    );
  }
  return config.hasRedirectAfterPasswordReset;
}

export function getDefaultAccountId(): string | null {
  return config.defaultAccountId || null;
}
